if (!customElements.get("product-tabs")) {
  class ProductTabs extends HTMLElement {
    constructor() {
      super();
      this.id = this.getAttribute("id");

      if (Shopify.designMode) {
        window.addEventListener(
          "shopify:section:load",
          this.init.bind(this)
        );
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.querySelectorAll(".product__tab--trigger").forEach(
        tabTrigger => {
          tabTrigger.addEventListener("click", evt => {
            this.openTab(
              evt,
              tabTrigger.dataset.tab
            );
          });
        }
      );

      this.initCollapsibleContent();
      this.openDefaultTab();
    }

    openDefaultTab() {
      const event = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true
      });

      this.querySelector(
        ".product__tab[data-default-open]"
      )?.dispatchEvent(event);
    }

    openTab(evt, tabName) {
      const tabcontents = this.querySelectorAll(
        ".product__tab-content"
      );
      tabcontents.forEach(content => {
        content.style.display = "none";
      });

      const tablinks = this.querySelectorAll(".product__tab");
      tablinks.forEach(link => {
        link.classList.remove("active");
      });

      const currentTabContent = this.querySelector(`#${tabName}`);

      if (currentTabContent) {
        currentTabContent.style.display = "block";
        if (evt.currentTarget) {
          evt.currentTarget.classList.add("active");
        }
      }
    }

    initCollapsibleContent() {
      this.querySelectorAll(
        ".product__tab-content--collapsible"
      ).forEach(wrapper => {
        if (wrapper.dataset.collapsibleReady === "1") return;

        const inner = wrapper.querySelector(
          ".product__tab-content-inner"
        );
        const toggle = wrapper.querySelector(
          ".product__tab-content-toggle"
        );
        if (!inner || !toggle) return;

        const wordLimit =
          parseInt(wrapper.dataset.wordLimit, 10) || 50;
        const expandLabel =
          wrapper.dataset.expandLabel || "Expand description";
        const collapseLabel =
          wrapper.dataset.collapseLabel || "Minimize description";

        const fullHTML = inner.innerHTML;
        const truncatedHTML = this.truncateHTMLByWords(
          fullHTML,
          wordLimit
        );

        if (truncatedHTML === null) {
          wrapper.classList.remove(
            "product__tab-content--collapsible",
            "is-collapsed"
          );
          toggle.style.display = "none";
          return;
        }

        inner.dataset.fullContent = fullHTML;
        inner.dataset.truncatedContent = truncatedHTML;
        inner.innerHTML = truncatedHTML;

        const labelEl = toggle.querySelector(
          ".product__tab-content-toggle-label"
        );

        const FADE_MS = 180;
        const HEIGHT_MS = 400;
        let isAnimating = false;

        toggle.addEventListener("click", () => {
          if (isAnimating) return;
          isAnimating = true;

          const isCollapsed =
            wrapper.classList.contains("is-collapsed");
          const targetHTML = isCollapsed
            ? inner.dataset.fullContent
            : inner.dataset.truncatedContent;

          const startHeight = inner.offsetHeight;
          inner.style.maxHeight = startHeight + "px";
          inner.style.overflow = "hidden";
          inner.classList.add("is-fading");

          if (isCollapsed) {
            wrapper.classList.remove("is-collapsed");
            wrapper.classList.add("is-expanded");
            toggle.setAttribute("aria-expanded", "true");
            if (labelEl) labelEl.textContent = collapseLabel;
          } else {
            wrapper.classList.remove("is-expanded");
            wrapper.classList.add("is-collapsed");
            toggle.setAttribute("aria-expanded", "false");
            if (labelEl) labelEl.textContent = expandLabel;
          }

          window.setTimeout(() => {
            inner.innerHTML = targetHTML;

            const prevMaxHeight = inner.style.maxHeight;
            inner.style.maxHeight = "none";
            const endHeight = inner.offsetHeight;
            inner.style.maxHeight = prevMaxHeight;

            void inner.offsetHeight;

            inner.style.maxHeight = endHeight + "px";
            inner.classList.remove("is-fading");

            window.setTimeout(() => {
              inner.style.maxHeight = "";
              inner.style.overflow = "";
              isAnimating = false;
            }, HEIGHT_MS);
          }, FADE_MS);
        });

        wrapper.dataset.collapsibleReady = "1";
      });
    }

    truncateHTMLByWords(html, limit) {
      const container = document.createElement("div");
      container.innerHTML = html;

      let wordsLeft = limit;
      let totalWords = 0;
      const countOnly = document.createElement("div");
      countOnly.innerHTML = html;
      totalWords = (countOnly.textContent || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;

      if (totalWords <= limit) return null;

      const walk = node => {
        if (wordsLeft <= 0) {
          node.parentNode && node.parentNode.removeChild(node);
          return;
        }
        if (node.nodeType === Node.TEXT_NODE) {
          const words = node.textContent.split(/(\s+)/);
          const kept = [];
          for (const w of words) {
            if (/\s+/.test(w)) {
              kept.push(w);
              continue;
            }
            if (w === "") continue;
            if (wordsLeft <= 0) break;
            kept.push(w);
            wordsLeft -= 1;
          }
          node.textContent = kept.join("");
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const children = Array.from(node.childNodes);
          for (const child of children) {
            if (wordsLeft <= 0) {
              child.parentNode &&
                child.parentNode.removeChild(child);
            } else {
              walk(child);
            }
          }
        }
      };

      walk(container);

      const lastText = this.findLastTextNode(container);
      if (lastText) {
        lastText.textContent =
          lastText.textContent.replace(/\s+$/, "") + "…";
      }

      return container.innerHTML;
    }

    findLastTextNode(node) {
      for (let i = node.childNodes.length - 1; i >= 0; i--) {
        const child = node.childNodes[i];
        if (
          child.nodeType === Node.TEXT_NODE &&
          child.textContent.trim()
        ) {
          return child;
        }
        if (child.nodeType === Node.ELEMENT_NODE) {
          const found = this.findLastTextNode(child);
          if (found) return found;
        }
      }
      return null;
    }
  }
  customElements.define("product-tabs", ProductTabs);
}
